import { useAtom } from 'jotai';
import { 
  loadingAtom, 
  errorAtom, 
  tutorialStateAtom, 
  globalLoadingAtom,
  themeAtom,
  themeEffectAtom,
  aiInsightsAtom,
  authAtom,
  preferencesAtom
} from './atoms';
import type { TutorialState, Theme, AIInsightsState, AuthState, UserPreferences } from '../utils/types';

// Loading hooks
export function useLoading() {
  return useAtom(loadingAtom);
}

export function useError() {
  return useAtom(errorAtom);
}

// Tutorial hooks
export function useTutorialState() {
  return useAtom(tutorialStateAtom);
}

export function useUpdateTutorial() {
  const [, setTutorial] = useTutorialState();
  return (update: Partial<TutorialState>) => setTutorial(prev => ({ ...prev, ...update }));
}

// Global loading hooks
export function useGlobalLoading() {
  return useAtom(globalLoadingAtom);
}

// Theme hooks
export function useTheme() {
  const [theme] = useAtom(themeAtom);
  const [, setThemeWithEffect] = useAtom(themeEffectAtom);
  return [theme, setThemeWithEffect] as const;
}

export function useUpdateTheme() {
  const [, setTheme] = useAtom(themeEffectAtom);
  return (theme: Theme) => setTheme(theme);
}

// AI Insights hooks
export function useAIInsights() {
  return useAtom(aiInsightsAtom);
}

export function useUpdateAIInsights() {
  const [, setAIInsights] = useAIInsights();
  return (update: Partial<AIInsightsState>) => setAIInsights(prev => ({ ...prev, ...update }));
}

// Auth hooks
export function useAuth() {
  return useAtom(authAtom);
}

export function useUpdateAuth() {
  const [, setAuth] = useAuth();
  return (update: Partial<AuthState>) => setAuth(prev => ({ ...prev, ...update }));
}

// Preferences hooks
export function usePreferences() {
  return useAtom(preferencesAtom);
}

export function useUpdatePreferences() {
  const [, setPreferences] = usePreferences();
  return (update: Partial<UserPreferences>) => setPreferences(prev => ({ ...prev, ...update }));
}